import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Divider,
  Grid,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchTeacherApprovedCourses } from "../../../redux/Actions/TeacherApprovedCoursesAction";
import { fetchCourseTree } from "../../../redux/Actions/courseTreeAction";
import {
  addLesson,
  clearLessons,
  fetchLessons,
  updateZoomLinkAction,
} from "../../../redux/Actions/lessonActions";
import MessageCard from "../../MessageCard/MessageCard";
import EditCourseModal from "./EditCourseModal";
import Lesson from "./Lesson";
import TeacherNavbar from "./TeacherNavbar";

const ManageCourse = () => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth.user.userId);

  const { approvedCourses } = useSelector(
    (state) => state.teacherApprovedCourses
  );
  const { treeData } = useSelector((state) => state.courseTree);

  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [openLessonModal, setOpenLessonModal] = useState(false);
  const [lessonNumber, setLessonNumber] = useState("");
  const [openLessonEditModal, setOpenLessonEditModal] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [zoomLinkToUpdate, setZoomLinkToUpdate] = useState("");
  const [zoomUpdateFeedback, setZoomUpdateFeedback] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [courseToEdit, setCourseToEdit] = useState(null);

  const lessonItems = useSelector((state) => state.fetchLesson?.lessons);
  const lessons = useMemo(() => lessonItems || [], [lessonItems]);

  const formatSchedule = (course) => {
    if (course?.dayTimes?.length) {
      return course.dayTimes
        .map((dt) => `${dt.dayOfWeek} ${dt.startTime} - ${dt.endTime}`)
        .join(", ");
    }
    if (course?.daysOfWeek?.length && course?.startTime && course?.endTime) {
      const days = course.daysOfWeek
        .map((d) => d.toLowerCase().replace(/^\w/, (c) => c.toUpperCase()))
        .join(", ");
      return `${days} ${course.startTime} - ${course.endTime}`;
    }
    return "N/A";
  };

  const getCourseLevel = (course) => {
    const title = course?.courseTitle || "";
    return (
      course?.parentTitle ||
      title.match(/^[A-C][0-2]/i)?.[0]?.toUpperCase() ||
      title ||
      "N/A"
    );
  };

  useEffect(() => {
    dispatch(fetchTeacherApprovedCourses(userId));
    dispatch(fetchCourseTree());
  }, [dispatch, userId]);

  const handleSelectNode = (node) => {
    setSelectedNode(node);
    setSelectedCourseId(null);
  };

  const getFilteredCourses = () => {
    if (!selectedNode) return [];
    return approvedCourses.filter(
      (course) => getCourseLevel(course) === selectedNode.title
    );
  };

  const filteredCourses = getFilteredCourses();

  const handleCourseClick = (courseId) => {
    const toggled = selectedCourseId === courseId ? null : courseId;
    setSelectedCourseId(toggled);
    if (toggled) {
      dispatch(fetchLessons(toggled));
      dispatch(clearLessons());
    }
  };

  useEffect(() => {
    if (lessons.length > 0) {
      const maxOrder = Math.max(...lessons.map((l) => l.orderNumber));
      setLessonNumber((maxOrder + 1).toString());
    } else {
      setLessonNumber("1");
    }
  }, [lessons]);

  const handleAddLesson = () => {
    if (lessonNumber && selectedCourseId) {
      dispatch(addLesson(selectedCourseId, parseInt(lessonNumber))).then(() => {
        dispatch(fetchLessons(selectedCourseId));
        setLessonNumber("");
        setOpenLessonModal(false);
      });
    }
  };

  const handleLessonClick = (lesson) => {
    setSelectedLesson(lesson);
    setOpenLessonEditModal(true);
  };

  const onEditModalClose = () => {
    setOpenEditModal(false);
    setCourseToEdit(null);
    dispatch(fetchTeacherApprovedCourses(userId));
  };

  return (
    <Box sx={{ display: "flex" }}>
      <TeacherNavbar />
      <Box
        sx={{
          ml: { xs: 0, md: "250px" },
          px: { xs: 2, md: 6 },
          py: 3,
          width: { xs: "100%", md: "calc(100% - 250px)" },
          bgcolor: "#f8f9fa",
          minHeight: "100vh",
          boxSizing: "border-box",
          overflowY: "auto",
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{ color: "#0077b6", mb: 4, fontWeight: "bold" }}
        >
          Manage Only Approved Courses
        </Typography>

        <Box sx={{ display: "flex", gap: 4, flexWrap: "nowrap" }}>
          {/* Left tree */}
          <Box sx={{ width: { xs: "100%", md: "25%" }, mt: 7 }}>
            <Typography variant="h6" gutterBottom>
              Course Tree
            </Typography>
            {treeData.map((node, index) => (
              <Box key={index} mb={1}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => handleSelectNode(node)}
                >
                  {node.title}
                </Button>
              </Box>
            ))}
          </Box>

          <Divider orientation="vertical" flexItem sx={{ mr: -2 }} />

          {/* Right content */}
          <Box sx={{ flexGrow: 1 }}>
            {!selectedNode ? (
              <Typography>Please select a course from the tree.</Typography>
            ) : filteredCourses.length === 0 ? (
              <Typography>No approved courses under this selection.</Typography>
            ) : (
              <Box>
                {filteredCourses.map((course) => (
                  <Button
                    key={course.teacherCourseId}
                    variant="contained"
                    onClick={() => handleCourseClick(course.teacherCourseId)}
                    sx={{
                      display: "block",
                      mb: 2,
                      mx: "auto",
                      bgcolor:
                        selectedCourseId === course.teacherCourseId
                          ? "#066191"
                          : "#0077b6",
                      color: "white",
                      maxWidth: 400,
                      "&:hover": { bgcolor: "#066191" },
                    }}
                  >
                    {getCourseLevel(course)} ({course.gnumber})
                  </Button>
                ))}

                {filteredCourses.map((course) => (
                  <Collapse
                    key={course.teacherCourseId}
                    in={selectedCourseId === course.teacherCourseId}
                  >
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      {/* Course Info Card */}
                      <Grid item xs={12} md={6}>
                        <Card
                          sx={{
                            borderRadius: 4,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                            border: "1px solid #e0e0e0",
                            height: "100%",
                            bgcolor: "aliceblue",
                          }}
                        >
                          <CardContent sx={{ p: 4 }}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={3}
                            >
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 700,
                                  color: "#1a237e",
                                  letterSpacing: "-0.5px",
                                }}
                              >
                                Course Information
                              </Typography>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() => {
                                  setCourseToEdit(course);
                                  setOpenEditModal(true);
                                }}
                                sx={{
                                  borderRadius: 20,
                                  textTransform: "none",
                                  fontWeight: 600,
                                  borderWidth: 2,
                                  "&:hover": { borderWidth: 2 },
                                }}
                              >
                                Edit Details
                              </Button>
                            </Box>

                            <Grid container spacing={3}>
                              {[
                                {
                                  label: "Course Level",
                                  value: `${getCourseLevel(course)} (${course.gnumber})`,
                                },
                                {
                                  label: "Course Record",
                                  value: course.courseTitle || "N/A",
                                },
                                {
                                  label: "Course Type",
                                  value: course.courseType,
                                },
                                {
                                  label: "Schedule",
                                  value: formatSchedule(course),
                                  col: 12,
                                },
                                {
                                  label: "Duration",
                                  value: `${new Date(
                                    course.startDate
                                  ).toLocaleDateString()} - ${new Date(
                                    course.endDate
                                  ).toLocaleDateString()}`,
                                  col: 12,
                                },
                              ].map((item, idx) => (
                                <Grid item xs={12} sm={item.col || 6} key={idx}>
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "text.secondary",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                        display: "block",
                                        mb: 0.5,
                                      }}
                                    >
                                      {item.label}
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        color: "#263238",
                                        fontWeight: 500,
                                        fontSize: "1rem",
                                      }}
                                    >
                                      {item.value || "-"}
                                    </Typography>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Lesson Card */}
                      <Grid item xs={12} md={6}>
                        <Card
                          sx={{
                            p: 2,
                            bgcolor: "aliceblue",
                            borderRadius: 2,
                            boxShadow: 3,
                          }}
                        >
                          <CardContent>
                            <Button
                              variant="outlined"
                              onClick={() => setOpenLessonModal(true)}
                            >
                              Add Lesson
                            </Button>
                            <Typography variant="h6" sx={{ mt: 2 }}>
                              Created Lessons
                            </Typography>
                            <Grid container spacing={2}>
                              {lessons.map((lesson) => (
                                <Grid item xs={12} key={lesson.id}>
                                  <Card
                                    onClick={() => handleLessonClick(lesson)}
                                    sx={{
                                      cursor: "pointer",
                                      "&:hover": { bgcolor: "#f6f8f8" },
                                    }}
                                  >
                                    <CardContent>
                                      <Typography>
                                        Lesson #{lesson.orderNumber}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Collapse>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Add Lesson Modal */}
      <Modal open={openLessonModal} onClose={() => setOpenLessonModal(false)}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <Paper sx={{ p: 4, width: "fit-content", minWidth: 300 }}>
            <Typography variant="h6">Enter Lesson Number</Typography>
            <TextField
              fullWidth
              margin="normal"
              label="Lesson Number"
              type="number"
              value={lessonNumber}
              disabled
            />
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                onClick={() => setOpenLessonModal(false)}
              >
                Cancel
              </Button>
              <Button variant="contained" onClick={handleAddLesson}>
                Add
              </Button>
            </Box>
          </Paper>
        </Box>
      </Modal>

      {/* Edit Lesson Modal */}
      <Modal
        open={openLessonEditModal}
        onClose={() => {
          setOpenLessonEditModal(false);
          setZoomUpdateFeedback(null);
          setZoomLinkToUpdate("");
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="90vh"
          py={6}
        >
          <Paper sx={{ p: 4, width: "fit-content", minWidth: 400 }}>
            <Typography variant="h6" gutterBottom>
              Edit Lesson #{selectedLesson?.orderNumber}
            </Typography>
            {selectedLesson && (
              <Lesson
                lesson={{
                  id: selectedLesson.id,
                  number: selectedLesson.orderNumber,
                  zoomLink: selectedLesson.zoomLink,
                }}
                teacherCourseId={selectedCourseId}
                onZoomLinkChange={setZoomLinkToUpdate}
              />
            )}

            {zoomUpdateFeedback && (
              <Box mt={2}>
                <MessageCard
                  message={zoomUpdateFeedback.message}
                  type={zoomUpdateFeedback.type}
                  onClose={() => setZoomUpdateFeedback(null)}
                />
              </Box>
            )}

            <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <Button
                variant="outlined"
                onClick={() => setOpenLessonEditModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  if (
                    selectedLesson?.id &&
                    selectedCourseId &&
                    zoomLinkToUpdate
                  ) {
                    dispatch(
                      updateZoomLinkAction(
                        selectedLesson.id,
                        selectedCourseId,
                        zoomLinkToUpdate
                      )
                    )
                      .then(() => {
                        setZoomUpdateFeedback({
                          type: "success",
                          message: "Zoom link updated successfully.",
                        });
                        dispatch(fetchLessons(selectedCourseId));
                        setTimeout(() => {
                          setZoomUpdateFeedback(null);
                          setZoomLinkToUpdate("");
                          setOpenLessonEditModal(false);
                        }, 1000);
                      })
                      .catch(() => {
                        setZoomUpdateFeedback({
                          type: "error",
                          message: "Failed to update Zoom link.",
                        });
                      });
                  }
                }}
              >
                Update
              </Button>
            </Box>
          </Paper>
        </Box>
      </Modal>
      {courseToEdit && (
        <EditCourseModal
          open={openEditModal}
          onClose={onEditModalClose}
          course={courseToEdit}
        />
      )}
    </Box>
  );
};

export default ManageCourse;
